import React, { useState, useEffect } from 'react';
import {
  Grid,
  Box,
  Typography,
  CircularProgress,
  Paper,
  Tabs,
  Tab
} from '@mui/material';
import {
  Assessment,
  People,
  Flight,
  TrendingUp,
  AccountTree,
  Settings
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { ROLES } from '../constants/roles';
import { hasSuperAdminView } from '../utils/rolePermissions';
import StatCard from './MISDashboardComponents/StatCard';
import AnalyticalTreeTab from './Analytics/AnalyticalTree/AnalyticalTreeTab';
import AnalyticalRules from './Analytics/AnalyticalRules';
import MaintenanceAlertsCard from './MISDashboardComponents/MaintenanceAlertsCard';
import RecentFlightLogsCard from './MISDashboardComponents/RecentFlightLogsCard';
import CertificationAlertsCard from './MISDashboardComponents/CertificationAlertsCard';
import ComplianceMetricsCard from './MISDashboardComponents/ComplianceMetricsCard';
import UserSummaryCard from './MISDashboardComponents/UserSummaryCard';
import AircraftUtilizationCard from './MISDashboardComponents/AircraftUtilizationCard';

const MISDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  // User Summary
  const [userSummary, setUserSummary] = useState({
    totalUsers: 0,
    activeUsers: 0,
    usersByRole: {},
    totalRoles: 0
  });

  // Certification Expiry Alerts
  const [certificationAlerts, setCertificationAlerts] = useState({
    expiring30Days: [],
    expiring60Days: [],
    expired: []
  });

  // Flight Operations Metrics
  const [flightMetrics, setFlightMetrics] = useState({
    totalFlightHours: 0,
    completionRate: 0,
    totalTrips: 0,
    completedTrips: 0
  });

  // Aircraft Utilization
  const [aircraftUtilization, setAircraftUtilization] = useState([]);

  // Maintenance Alerts
  const [maintenanceAlerts, setMaintenanceAlerts] = useState({
    overdue: [],
    upcoming: []
  });

  // Recent Flight Log Entries
  const [recentFlightLogs, setRecentFlightLogs] = useState([]);

  // Compliance Metrics
  const [complianceMetrics, setComplianceMetrics] = useState({
    totalDuties: 0,
    compliantDuties: 0,
    nonCompliantDuties: 0,
    byAircraftType: {
      'Fixed Wing': { total: 0, compliant: 0, nonCompliant: 0 },
      'Helicopter': { total: 0, compliant: 0, nonCompliant: 0 }
    }
  });

  const [tabValue, setTabValue] = useState('dashboard');

  useEffect(() => {
    if (user) {
      fetchDashboardData();
    }
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const promises = [];

      // Fetch users for summary
      promises.push(
        axios.get('/api/users', { headers }).catch(err => {
          if (err.response?.status !== 403) {
            console.error('Error fetching users:', err);
          }
          return { data: [] };
        })
      );

      // Fetch trips for completion rate
      promises.push(
        axios.get('/api/sales/trips', { headers }).catch(err => {
          if (err.response?.status !== 403) {
            console.error('Error fetching trips:', err);
          }
          return { data: [] };
        })
      );

      // Fetch flight log summary for total hours
      promises.push(
        axios.get('/api/crew/flight-logs/summary', { headers }).catch(err => {
          if (err.response?.status !== 403) {
            console.error('Error fetching flight log summary:', err);
          }
          return { data: { totalFlightHours: 0, totalEntries: 0 } };
        })
      );

      // Fetch aircraft for utilization
      promises.push(
        axios.get('/api/aircraft', { headers }).catch(err => {
          if (err.response?.status !== 403) {
            console.error('Error fetching aircraft:', err);
          }
          return { data: [] };
        })
      );

      // Fetch maintenance for alerts
      promises.push(
        axios.get('/api/maintenance', { headers }).catch(err => {
          if (err.response?.status !== 403) {
            console.error('Error fetching maintenance:', err);
          }
          return { data: [] };
        })
      );

      // Fetch recent flight log entries
      promises.push(
        axios.get('/api/crew/flight-logs/recent', { headers }).catch(err => {
          if (err.response?.status !== 403) {
            console.error('Error fetching recent flight logs:', err);
          }
          return { data: [] };
        })
      );

      // Fetch duty roster entries for compliance metrics
      promises.push(
        axios.get('/api/ops/crew-duty-roster', { headers }).catch(err => {
          if (err.response?.status !== 403) {
            console.error('Error fetching duty roster entries:', err);
          }
          return { data: [] };
        })
      );

      const [usersRes, tripsRes, flightLogSummaryRes, aircraftRes, maintenanceRes, flightLogsRes, dutyRosterRes] = await Promise.all(promises);

      const users = usersRes?.data || [];
      const trips = tripsRes?.data || [];
      const flightLogSummary = flightLogSummaryRes?.data || { totalFlightHours: 0, totalEntries: 0 };
      const aircraft = aircraftRes?.data || [];
      const maintenance = maintenanceRes?.data || [];
      const flightLogs = flightLogsRes?.data || [];
      const dutyRosterEntries = dutyRosterRes?.data || [];

      // Calculate User Summary
      if (users.length > 0) {
        const activeUsers = users.filter(u => u.isActive === true);
        const usersByRole = {};
        users.forEach(u => {
          usersByRole[u.role] = (usersByRole[u.role] || 0) + 1;
        });
        const totalRoles = Object.keys(usersByRole).length;

        setUserSummary({
          totalUsers: users.length,
          activeUsers: activeUsers.length,
          usersByRole,
          totalRoles
        });

        // Calculate Certification Expiry Alerts
        const now = new Date();
        const thirtyDays = new Date(now);
        thirtyDays.setDate(thirtyDays.getDate() + 30);
        const sixtyDays = new Date(now);
        sixtyDays.setDate(sixtyDays.getDate() + 60);

        const expiring30Days = [];
        const expiring60Days = [];
        const expired = [];

        users.forEach(user => {
          // Check certificates
          if (user.certificates && Array.isArray(user.certificates)) {
            user.certificates.forEach(cert => {
              if (!cert.hasNoExpiry && cert.expiryDate) {
                const expiryDate = new Date(cert.expiryDate);
                if (expiryDate < now) {
                  expired.push({
                    type: 'certificate',
                    userId: user._id,
                    userName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
                    itemName: cert.name,
                    itemType: cert.type,
                    expiryDate: cert.expiryDate
                  });
                } else if (expiryDate <= thirtyDays) {
                  expiring30Days.push({
                    type: 'certificate',
                    userId: user._id,
                    userName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
                    itemName: cert.name,
                    itemType: cert.type,
                    expiryDate: cert.expiryDate
                  });
                } else if (expiryDate <= sixtyDays) {
                  expiring60Days.push({
                    type: 'certificate',
                    userId: user._id,
                    userName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
                    itemName: cert.name,
                    itemType: cert.type,
                    expiryDate: cert.expiryDate
                  });
                }
              }
            });
          }

          // Check trainings
          if (user.ongoingTrainings && Array.isArray(user.ongoingTrainings)) {
            user.ongoingTrainings.forEach(training => {
              if (!training.hasNoExpiry && training.expiryDate) {
                const expiryDate = new Date(training.expiryDate);
                if (expiryDate < now) {
                  expired.push({
                    type: 'training',
                    userId: user._id,
                    userName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
                    itemName: training.trainingName,
                    itemType: training.trainingType || 'Training',
                    expiryDate: training.expiryDate
                  });
                } else if (expiryDate <= thirtyDays) {
                  expiring30Days.push({
                    type: 'training',
                    userId: user._id,
                    userName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
                    itemName: training.trainingName,
                    itemType: training.trainingType || 'Training',
                    expiryDate: training.expiryDate
                  });
                } else if (expiryDate <= sixtyDays) {
                  expiring60Days.push({
                    type: 'training',
                    userId: user._id,
                    userName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
                    itemName: training.trainingName,
                    itemType: training.trainingType || 'Training',
                    expiryDate: training.expiryDate
                  });
                }
              }
            });
          }
        });

        setCertificationAlerts({
          expiring30Days: expiring30Days.sort((a, b) => new Date(a.expiryDate) - new Date(b.expiryDate)),
          expiring60Days: expiring60Days.sort((a, b) => new Date(a.expiryDate) - new Date(b.expiryDate)),
          expired: expired.sort((a, b) => new Date(b.expiryDate) - new Date(a.expiryDate))
        });
      }

      // Calculate Flight Metrics
      const totalFlightHours = parseFloat(flightLogSummary.totalFlightHours || 0);

      // Calculate completion rate
      const activeTrips = trips.filter(t => 
        !['DRAFT', 'CANCELLED'].includes(t.status)
      );
      const completedTrips = trips.filter(t => 
        ['COMPLETED', 'CHARTER_COMPLETED'].includes(t.status)
      );
      const completionRate = activeTrips.length > 0 
        ? ((completedTrips.length / activeTrips.length) * 100).toFixed(1)
        : 0;

      setFlightMetrics({
        totalFlightHours: totalFlightHours.toFixed(2),
        completionRate: parseFloat(completionRate),
        totalTrips: activeTrips.length,
        completedTrips: completedTrips.length
      });

      // Calculate Aircraft Utilization
      // This would ideally come from a summary endpoint that aggregates flight hours per aircraft
      // For now, we'll show a placeholder structure
      const utilizationData = aircraft.map(ac => ({
        registration: ac.registration || ac.tailNumber || 'N/A',
        manufacturer: ac.manufacturer || 'N/A',
        model: ac.model || 'N/A',
        flightHours: ac.flightHours || 0,
        status: ac.status || 'UNKNOWN'
      })).sort((a, b) => (b.flightHours || 0) - (a.flightHours || 0));

      setAircraftUtilization(utilizationData);

      // Calculate Maintenance Alerts (Overdue and Upcoming)
      const now = new Date();
      const next30Days = new Date();
      next30Days.setDate(next30Days.getDate() + 30);

      const overdueMaintenance = maintenance.filter(m => 
        m.status === 'SCHEDULED' && 
        m.scheduledDate && 
        new Date(m.scheduledDate) < now
      );

      const upcomingMaintenance = maintenance.filter(m => 
        m.status === 'SCHEDULED' && 
        m.scheduledDate && 
        new Date(m.scheduledDate) >= now && 
        new Date(m.scheduledDate) <= next30Days
      ).sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate));

      setMaintenanceAlerts({
        overdue: overdueMaintenance,
        upcoming: upcomingMaintenance.slice(0, 10) // Show next 10 upcoming
      });

      // Set Recent Flight Logs
      setRecentFlightLogs(flightLogs.slice(0, 5)); // Show last 5 entries

      // Calculate Compliance Metrics with Aircraft Type Context
      const complianceNow = new Date();
      const thirtyDaysAgo = new Date(complianceNow.getTime() - 30 * 24 * 60 * 60 * 1000);
      
      // Filter recent duties (last 30 days)
      const recentDuties = dutyRosterEntries.filter(entry => {
        const dutyDate = new Date(entry.dutyStartTime);
        return dutyDate >= thirtyDaysAgo && ['SCHEDULED', 'IN_PROGRESS', 'COMPLETED'].includes(entry.status);
      });

      const complianceByAircraftType = {
        'Fixed Wing': { total: 0, compliant: 0, nonCompliant: 0 },
        'Helicopter': { total: 0, compliant: 0, nonCompliant: 0 }
      };

      recentDuties.forEach(entry => {
        const aircraftType = entry.aircraftType || 'Helicopter';
        if (complianceByAircraftType[aircraftType]) {
          complianceByAircraftType[aircraftType].total++;
          if (entry.isNonCompliant) {
            complianceByAircraftType[aircraftType].nonCompliant++;
          } else {
            complianceByAircraftType[aircraftType].compliant++;
          }
        }
      });

      const totalDuties = recentDuties.length;
      const compliantDuties = recentDuties.filter(e => !e.isNonCompliant).length;
      const nonCompliantDuties = recentDuties.filter(e => e.isNonCompliant).length;

      setComplianceMetrics({
        totalDuties,
        compliantDuties,
        nonCompliantDuties,
        byAircraftType: complianceByAircraftType
      });

      setLastUpdated(new Date());
      setLoading(false);
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Error fetching MIS dashboard data:', error);
      }
      setLastUpdated(new Date());
      setLoading(false);
    }
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        MIS Dashboard
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Management Information System - Analytics and Reporting
      </Typography>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab
            icon={<Assessment />}
            iconPosition="start"
            label="Dashboard"
            value="dashboard"
          />
          {(hasSuperAdminView(user?.role) || user?.role === ROLES.MIS) && (
            <Tab
              icon={<AccountTree />}
              iconPosition="start"
              label="Analytical Tree"
              value="analyticalTree"
            />
          )}
          {(hasSuperAdminView(user?.role) || user?.role === ROLES.MIS) && (
            <Tab
              icon={<Settings />}
              iconPosition="start"
              label="Analytical Rules"
              value="analyticalRules"
            />
          )}
        </Tabs>
      </Paper>

      {tabValue === 'dashboard' && (
        <>
      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Flight Hours"
            value={flightMetrics.totalFlightHours}
            icon={<Flight fontSize="large" />}
            color="primary"
            subtitle="From flight logs"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Completion Rate"
            value={`${flightMetrics.completionRate}%`}
            icon={<TrendingUp fontSize="large" />}
            color="success"
            subtitle={`${flightMetrics.completedTrips} of ${flightMetrics.totalTrips} trips`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={userSummary.totalUsers}
            icon={<People fontSize="large" />}
            color="info"
            subtitle={`${userSummary.activeUsers} active`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Roles"
            value={userSummary.totalRoles}
            icon={<Assessment fontSize="large" />}
            color="secondary"
            subtitle="In use"
          />
        </Grid>
      </Grid>

      <MaintenanceAlertsCard maintenanceAlerts={maintenanceAlerts} />
      <RecentFlightLogsCard recentFlightLogs={recentFlightLogs} />
      <CertificationAlertsCard certificationAlerts={certificationAlerts} />
      <ComplianceMetricsCard complianceMetrics={complianceMetrics} />
      <UserSummaryCard userSummary={userSummary} />
      <AircraftUtilizationCard aircraftUtilization={aircraftUtilization} />

      {/* Last Updated Timestamp */}
      <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography 
          variant="caption" 
          color="textSecondary" 
          sx={{ display: 'block', textAlign: 'center' }}
        >
          Last updated: {lastUpdated 
            ? new Intl.DateTimeFormat('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                timeZoneName: 'short',
                hour12: false
              }).format(lastUpdated)
            : 'Never'}
        </Typography>
      </Box>
        </>
      )}

      {tabValue === 'analyticalTree' && (hasSuperAdminView(user?.role) || user?.role === ROLES.MIS) && (
        <AnalyticalTreeTab />
      )}
      {tabValue === 'analyticalRules' && (hasSuperAdminView(user?.role) || user?.role === ROLES.MIS) && (
        <AnalyticalRules />
      )}
    </Box>
  );
};

export default MISDashboard;

